import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import 'leaflet/dist/leaflet.css';
import './index.css';
import './styles/tokens.css';
import { RouteThemeProvider } from './contexts/RouteThemeProvider';
import { initialiseMediaDelivery } from './shared/config/mediaDeliveryStore';
import { initialiseMmlTileAvailability } from './shared/config/mmlTileAvailabilityInit';
import { registerApplicationSlots } from './shared/config/applicationSlots';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { shouldRetryQuery } from './api/retryPolicy';

// runtime-config.js is a required boot input, even when its configured object
// is intentionally empty in local development. If transport drops that script,
// leave #root untouched so the inline watchdog can retry and ultimately render
// the honest unavailable state; never boot against an implicit empty fallback
// (PR #671). The proof of life below is published INSIDE this guard for the
// same reason: it tells the watchdog a real application owns the root, and a
// shell that never booted must never be able to say that.
if (typeof window.__FORESTMODELER_RUNTIME_CONFIG__ === 'object') {
  void initialiseMediaDelivery();
  void initialiseMmlTileAvailability();
  registerApplicationSlots(lazyWithRetry);

  // Suppress Vite's eager rethrow for dynamic-import (code-split chunk) fetch
  // failures.
  //
  // Vite dispatches `vite:preloadError` on window when a lazily-imported chunk
  // fails to load. On the shared self-hosted CI stacks this fires when localhost
  // networking briefly recycles mid-fetch (Chromium net::ERR_NETWORK_CHANGED) while
  // co-resident jobs churn containers; real users hit the same class right after a
  // deploy (stale chunk hash) or on a flaky connection.
  //
  // Route chunks are recovered by lazyWithRetry via targeted re-imports and a
  // guarded last-resort reload. This handler only prevents Vite from turning the
  // transient fetch failure into an uncaught exception before React.lazy sees the
  // rejected import promise. Permanent failures still surface from lazyWithRetry
  // after its finite retry/reload budget is exhausted.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        retry: shouldRetryQuery,
      },
    },
  });

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <RouteThemeProvider>
            <App />
          </RouteThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );

  // Proof of life for the boot watchdog in index.html. Reaching this line means
  // the entry graph executed and React owns the root, which is true whatever the
  // DOM looks like for the next few frames: React's first commit can legitimately
  // produce zero ELEMENT children (a route that renders null, a redirect). Without
  // this flag the watchdog can only read the DOM, and an empty #root would let it
  // reload a live application underneath the person using it.
  window.__FM_APP_BOOTED__ = true;
}
